export const PROJECT_TABS = [
  { id: "all", label: "All projects" },
  { id: "draft", label: "Draft" },
  { id: "needs_review", label: "Needs review" },
  { id: "approved", label: "Approved" },
  { id: "scheduled", label: "Scheduled" },
];

export const PROJECT_LANES = [
  { id: "all", label: "Total projects", hint: "Campaigns and content hubs" },
  { id: "draft", label: "Draft", hint: "Still being shaped" },
  { id: "needs_review", label: "Needs review", hint: "Waiting for decisions" },
  { id: "approved", label: "Approved", hint: "Ready to schedule" },
  { id: "scheduled", label: "Scheduled", hint: "Calendar is loaded" },
];

const SUPPORTING_TYPES = new Set(["research_brief", "image_prompt", "execution_report"]);
const STATUS_PRIORITY = {
  needs_review: 0,
  draft: 1,
  approved: 2,
  scheduled: 3,
  published: 4,
  archived: 5,
};

function normalizeDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
}

function tokenize(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((token) => token.length > 3);
}

function getProjectTerms(project = {}) {
  return Array.from(new Set([
    ...tokenize(project.title),
    ...tokenize(project.description),
    ...(project.pillars || []).flatMap(tokenize),
  ]));
}

function getSearchText(project = {}) {
  return [
    project.title,
    project.description,
    project.status,
    project.content_type,
    project.owner,
    ...(project.pillars || []),
    ...(project.platforms || []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function countByStatus(assets = [], status) {
  return assets.filter((asset) => asset.status === status).length;
}

function getLinkedCalendarItems(projectAssets = [], calendarItems = []) {
  const assetIds = new Set(projectAssets.map((asset) => asset.id));
  return calendarItems
    .filter((item) => item.asset_id && assetIds.has(item.asset_id))
    .sort((a, b) => normalizeDate(a.scheduled_at) - normalizeDate(b.scheduled_at));
}

function getRelatedInbox(project = {}, inbox = []) {
  const terms = getProjectTerms(project);
  if (terms.length === 0) return [];

  return inbox
    .filter((item) => item.status !== "archived")
    .filter((item) => {
      const text = [
        item.title,
        item.preview,
        item.source,
        item.platform,
        item.asset_type,
        item.payload?.origin,
      ].filter(Boolean).join(" ").toLowerCase();
      return terms.some((term) => text.includes(term));
    })
    .sort((a, b) => normalizeDate(b.received_at) - normalizeDate(a.received_at));
}

export function getProjectNextAction(project = {}) {
  switch (project.status) {
    case "needs_review":
      return {
        label: "Review queue",
        tone: "review",
        hint: "Content needs a human decision before it moves forward.",
      };
    case "approved":
      return {
        label: "Schedule content",
        tone: "approved",
        hint: "Approved assets are ready for the calendar.",
      };
    case "scheduled":
      return {
        label: "Monitor calendar",
        tone: "scheduled",
        hint: "Publishing dates are already lined up.",
      };
    case "published":
      return {
        label: "Review results",
        tone: "published",
        hint: "Published assets are ready to analyze or repurpose.",
      };
    default:
      return {
        label: "Create assets",
        tone: "draft",
        hint: "Add drafts, sources, or another agent run.",
      };
  }
}

export function buildProjectHub({
  project,
  assets = [],
  calendarItems = [],
  inbox = [],
  runs = [],
} = {}) {
  if (!project) return null;

  const projectAssets = assets
    .filter((asset) => asset.project_id === project.id)
    .sort((a, b) => {
      const priority = (STATUS_PRIORITY[a.status] ?? 6) - (STATUS_PRIORITY[b.status] ?? 6);
      if (priority !== 0) return priority;
      return normalizeDate(b.updated_at) - normalizeDate(a.updated_at);
    });
  const deliverables = projectAssets.filter((asset) => !SUPPORTING_TYPES.has(asset.asset_type));
  const supporting = projectAssets.filter((asset) => SUPPORTING_TYPES.has(asset.asset_type));
  const linkedCalendar = getLinkedCalendarItems(projectAssets, calendarItems);
  const relatedInbox = getRelatedInbox(project, inbox);
  const projectRuns = runs
    .filter((run) => run.project_id === project.id)
    .sort((a, b) => normalizeDate(b.created_at || b.started_at) - normalizeDate(a.created_at || a.started_at));

  const metrics = {
    assets: projectAssets.length,
    deliverables: deliverables.length,
    supporting: supporting.length,
    needsReview: countByStatus(projectAssets, "needs_review"),
    approved: countByStatus(projectAssets, "approved"),
    scheduled: countByStatus(projectAssets, "scheduled"),
    published: countByStatus(projectAssets, "published"),
    relatedInbox: relatedInbox.length,
    runs: projectRuns.length,
  };

  return {
    ...project,
    metrics,
    progressPercent: Math.round((project.progress || 0) * 100),
    nextAction: getProjectNextAction(project),
    assets: projectAssets,
    deliverables,
    supporting,
    calendarItems: linkedCalendar,
    nextCalendarItem: linkedCalendar[0] || null,
    relatedInbox,
    runs: projectRuns,
  };
}

export function filterProjects(projects = [], filters = {}) {
  const tab = filters.tab || "all";
  const query = (filters.query || "").trim().toLowerCase();

  return projects
    .filter((project) => tab === "all" || project.status === tab)
    .filter((project) => !query || getSearchText(project).includes(query))
    .sort((a, b) => normalizeDate(b.updated_at) - normalizeDate(a.updated_at));
}

export function buildProjectsWorkspace({
  projects = [],
  assets = [],
  calendarItems = [],
  inbox = [],
  runs = [],
  filters = {},
} = {}) {
  const enriched = filterProjects(projects, filters).map((project) => (
    buildProjectHub({ project, assets, calendarItems, inbox, runs })
  ));

  const summary = {
    total: projects.length,
    active: projects.filter((project) => project.status !== "archived").length,
    draft: projects.filter((project) => project.status === "draft").length,
    needsReview: projects.filter((project) => project.status === "needs_review").length,
    approved: projects.filter((project) => project.status === "approved").length,
    scheduled: projects.filter((project) => project.status === "scheduled").length,
    assets: assets.filter((asset) => asset.project_id).length,
  };

  const lanes = PROJECT_LANES.map((lane) => ({
    ...lane,
    count: lane.id === "all" ? summary.total : projects.filter((project) => project.status === lane.id).length,
  }));

  return {
    summary,
    tabs: PROJECT_TABS,
    lanes,
    filtered: enriched,
  };
}
